import { AUTH_CONFIG } from '@src/config/config';
import { useEffect } from 'react';
import { useAuth } from './AuthProvider';
import { getAuthCode } from '@src/service/authService';
import { config } from '@uhg-abyss/web/tools/config';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { initTokenManager } from '@src/service/tokenManager';
import { logAuthEvent } from '@src/service/apiTelemetry';

const REDIRECT_URI = config('SSO_REDIRECT_URI');

export const VerifyToken = () => {
  const { setUser } = useAuth();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    console.info('VerifyToken: code from URL', code);
    if (code) {
      // Remove auth code from URL immediately — keeps the code out of browser history
      window.history.replaceState(null, '', window.location.pathname);
      // Guard against React Strict Mode double-invocation: mark exchange as in-progress
      // so the second effect run does not redirect to SSO while the async exchange is still in flight
      sessionStorage.setItem('auth_exchange_in_progress', 'true');
      exchangeCodeForToken(code);
    } else {
      // If a token exchange is already in progress (e.g. React Strict Mode second run),
      // do nothing — wait for the exchange to complete
      if (sessionStorage.getItem('auth_exchange_in_progress')) return;

      // No code in URL - check if user is already authenticated
      const existingToken = sessionStorage.getItem('access_token');
      if (existingToken) {
        // Already logged in, go to app
        window.location.href = '/';
      } else {
        // Not authenticated, redirect to SSO
        window.location.href = '/auth/sso';
      }
    }
  }, []);

  const exchangeCodeForToken = async (code: string) => {
    try {
      const code_verifier = sessionStorage.getItem('pkce_verifier') || '';
      if (!code_verifier) {
        sessionStorage.removeItem('auth_exchange_in_progress');
        window.location.href = '/auth/sso';
        return;
      }

      const body = {
        code,
        redirect_uri: REDIRECT_URI,
        code_verifier,
      };

      const tokenData = await getAuthCode(body);

      if (tokenData['access_token']) {
        sessionStorage.setItem('access_token', tokenData['access_token']);
        if (tokenData['refresh_token']) {
          sessionStorage.setItem('refresh_token', tokenData['refresh_token']);
        }
        sessionStorage.removeItem('auth_exchange_in_progress');
        initTokenManager();
        logAuthEvent('Token exchange completed', 'success');
        fetchUserDetails(tokenData['access_token']);
      } else {
        console.error('Token response missing access_token', tokenData);
        logAuthEvent('Token exchange failed', 'failure', 'Token response missing access_token');
        sessionStorage.removeItem('auth_exchange_in_progress');
        window.location.href = '/auth/sso';
      }
    } catch (error) {
      console.error('Error fetching token', error);
      logAuthEvent('Token exchange failed', 'failure', error instanceof Error ? error.message : 'Token exchange error');
      sessionStorage.removeItem('auth_exchange_in_progress');
      window.location.href = '/auth/sso';
    }
  };

  const fetchUserDetails = async (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const userInfo = JSON.parse(jsonPayload);
      sessionStorage.setItem('User', JSON.stringify(userInfo));
      sessionStorage.setItem('login_time', new Date().toISOString());

      if (userInfo) {
        logAuthEvent('Sign-in completed', 'success');
        setUser(userInfo);
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error Fetching Details', error);
      logAuthEvent('Sign-in failed', 'failure', error instanceof Error ? error.message : 'User details parse error');
    }
  };

  return <>
      <LoadingOverlay
        loadingTitle="Please Wait"
        loadingMessage="Attempting to sign in"
        ariaLoadingLabel="Attempting to sign in"
        isLoading={true}
      >
        <div style={{ height: '100vh' }} />
      </LoadingOverlay>
    </>;
};
